/**
 * DQ Rules API Service
 * 
 * Real API service for data quality rules that calls the FastAPI backend 
 * with email-based authentication using the existing authManager pattern.
 */

import { httpClient, unwrapApiResult } from './authenticatedHttpClient';
import { authManager } from './authManager';
import { apiPath } from '../config/env';
import type {
    BatchCreateRulesRequest,
    BatchCreateDatabricksRulesRequest,
    BatchCreateRulesResponse,
    ModernBatchCreateRulesRequest,
    ModernBatchCreateRulesResponse,
    BackendDQRule,
    GetRulesResponse,
    UpdateRuleRequest,
    BatchDeleteRulesRequest,
    BatchDeleteRulesResponse
} from '../types/dqRules';

/**
 * Get current user email for API authentication
 * Uses centralized auth manager
 */
function getCurrentUserEmail(): string {
    return authManager.getUserEmail();
}

const dqRuleApiService = {
    /**
     * Create multiple DQ rules in a single batch request
     * Handles both data object rules and Databricks table rules
     * POST /api/v1/dq-rules/batch?user_email={email}
     */
    async createRulesBatch(request: BatchCreateRulesRequest | BatchCreateDatabricksRulesRequest): Promise<BatchCreateRulesResponse> {
        const userEmail = getCurrentUserEmail();
        const endpoint = apiPath(`dq-rules/batch?user_email=${encodeURIComponent(userEmail)}`);

        const response = await httpClient.post<BatchCreateRulesResponse>(endpoint, request);
        const result = unwrapApiResult(response);

        return result;
    },

    /**
     * Create multiple DQ rules using modern create-or-reuse logic
     * Supports both new rule creation and existing rule reuse
     * POST /api/v1/dq-rules/batch?user_email={email}
     */
    async createRulesBatchModern(request: ModernBatchCreateRulesRequest): Promise<ModernBatchCreateRulesResponse> {
        const userEmail = getCurrentUserEmail();
        const endpoint = apiPath(`dq-rules/batch?user_email=${encodeURIComponent(userEmail)}`);

        const response = await httpClient.post<ModernBatchCreateRulesResponse>(endpoint, request);
        return unwrapApiResult(response);
    },

    /**
     * Get all rules for a specific data object with optional user mapping
     * GET /api/v1/dq-rules/?data_object_id={id}&user_email={email}
     */
    async getRulesForDataObject(
        dataObjectId: string,
        options: { limit?: number; offset?: number } = {}
    ): Promise<GetRulesResponse> {
        const userEmail = getCurrentUserEmail();
        const params = new URLSearchParams();
        params.append('data_object_id', dataObjectId);
        params.append('user_email', userEmail);

        if (options.limit) params.append('limit', options.limit.toString());
        if (options.offset) params.append('offset', options.offset.toString());

        const endpoint = apiPath(`dq-rules/?${params.toString()}`);
        const response = await httpClient.get<BackendDQRule[]>(endpoint);
        const rules = unwrapApiResult(response);

        // Transform direct array response to expected GetRulesResponse format
        return {
            rules,
            total_count: rules.length,
            has_more: false // Backend doesn't provide pagination info currently
        };
    },

    /**
     * Get all available rules for the current user (own rules + public rules from others)
     * GET /api/v1/dq-rules/?user_email={email}
     */
    async getAllAvailableRules(
        options: { limit?: number; offset?: number } = {}
    ): Promise<GetRulesResponse> {
        const userEmail = getCurrentUserEmail();
        const params = new URLSearchParams();
        params.append('user_email', userEmail);

        if (options.limit) params.append('limit', options.limit.toString());
        if (options.offset) params.append('offset', options.offset.toString());

        const endpoint = apiPath(`dq-rules/?${params.toString()}`);
        const response = await httpClient.get<BackendDQRule[]>(endpoint);
        const rules = unwrapApiResult(response);

        // Transform direct array response to expected GetRulesResponse format
        return {
            rules,
            total_count: rules.length,
            has_more: false // Backend doesn't provide pagination info currently
        };
    },

    /**
     * Get a specific rule by its ID
     * GET /api/v1/dq-rules/{rule_id}?user_email={email}
     */
    async getRuleById(ruleId: string): Promise<BackendDQRule> {
        const userEmail = getCurrentUserEmail();
        const endpoint = apiPath(`dq-rules/${ruleId}?user_email=${encodeURIComponent(userEmail)}`);

        const response = await httpClient.get<BackendDQRule>(endpoint);
        return unwrapApiResult(response);
    },

    /**
     * Update an existing rule (enable/disable or description change)
     * PATCH /api/v1/dq-rules/{rule_id}?user_email={email}
     */
    async updateRule(ruleId: string, updates: UpdateRuleRequest): Promise<BackendDQRule> {
        const userEmail = getCurrentUserEmail();
        const endpoint = apiPath(`dq-rules/${ruleId}?user_email=${encodeURIComponent(userEmail)}`);

        const response = await httpClient.patch<BackendDQRule>(endpoint, updates);
        const result = unwrapApiResult(response);

        return result;
    },

    /**
     * Delete a single rule (user must be the creator)
     * DELETE /api/v1/dq-rules/{rule_id}?user_email={email}
     */
    async deleteRule(ruleId: string): Promise<void> {
        const userEmail = getCurrentUserEmail();
        const endpoint = apiPath(`dq-rules/${ruleId}?user_email=${encodeURIComponent(userEmail)}`);

        await httpClient.delete(endpoint);
    },

    /**
     * Delete multiple rules in batch with individual error handling
     * DELETE /api/v1/dq-rules/batch?user_email={email}
     */
    async deleteRulesBatch(ruleIds: string[]): Promise<BatchDeleteRulesResponse> {
        const userEmail = getCurrentUserEmail();
        const endpoint = apiPath(`dq-rules/batch?user_email=${encodeURIComponent(userEmail)}`);

        const request: BatchDeleteRulesRequest = { rule_ids: ruleIds };
        const response = await httpClient.delete<BatchDeleteRulesResponse>(endpoint, {
            body: JSON.stringify(request),
            headers: { 'Content-Type': 'application/json' }
        });
        return unwrapApiResult(response);
    }
};

export { dqRuleApiService };